import { CheckCircle2, Clipboard, ExternalLink, Printer, RadioTower, RefreshCw } from "lucide-react";
import { useEffect, useState } from "react";
import { api, type TagProvision } from "../api";
import type { InventoryUnit } from "../types";

export function ProvisionPage() {
  const [tag, setTag] = useState<TagProvision | null>(null);
  const [units, setUnits] = useState<InventoryUnit[]>([]);
  const [unitId, setUnitId] = useState("");
  const [message, setMessage] = useState("");
  const [working, setWorking] = useState(false);

  useEffect(() => {
    api.listInventory().then(setUnits);
  }, []);

  async function generate() {
    setWorking(true);
    try {
      setTag(await api.provisionTag());
      setMessage("");
    } catch (reason) {
      setMessage(reason instanceof Error ? reason.message : "Could not generate tag");
    } finally {
      setWorking(false);
    }
  }

  async function assign() {
    if (!tag || !unitId) return;
    setWorking(true);
    try {
      await api.assignTag(tag.token, unitId);
      setMessage("Tag assigned and activated. Any previous active tag was preserved as Replaced.");
      setUnits(await api.listInventory());
    } catch (reason) {
      setMessage(reason instanceof Error ? reason.message : "Could not assign tag");
    } finally {
      setWorking(false);
    }
  }

  return (
    <div className="page narrow-page">
      <div className="page-heading">
        <div>
          <h1>Provision NFC tag</h1>
          <p>Generate a secure NDEF URL, write it from an iPhone, then verify and assign it.</p>
        </div>
        <button className="button primary" onClick={generate} disabled={working}>
          <RadioTower size={19} /> Generate secure tag
        </button>
      </div>
      {!tag ? (
        <section className="provision-intro">
          <h2>Before you start</h2>
          <ol>
            <li>Use any NDEF-compatible adhesive tag such as NTAG213, NTAG215, or NTAG216.</li>
            <li>Open an NFC-writing application on the iPhone and choose a standard URL/URI record.</li>
            <li>Generate the URL here, copy it exactly, and write it to the tag.</li>
            <li>Scan once to verify Safari reaches this Windows PC, then assign the tag.</li>
          </ol>
        </section>
      ) : (
        <div className="provision-card">
          <div className="qr-frame">
            <img src={`/api/tags/${tag.token}/qr.png?size=10`} alt="QR code for the NFC URL" />
          </div>
          <div className="provision-details">
            <span>Secure application token</span>
            <strong>{tag.token_preview}</strong>
            <label>
              NFC URL
              <div className="copy-field">
                <input readOnly value={tag.url} onFocus={(event) => event.target.select()} />
                <button
                  className="icon-button"
                  onClick={() => {
                    navigator.clipboard.writeText(tag.url);
                    setMessage("URL copied");
                  }}
                  aria-label="Copy URL"
                >
                  <Clipboard />
                </button>
              </div>
            </label>
            <a className="button secondary" href={tag.url} target="_blank">
              <ExternalLink size={18} /> Verify URL
            </a>
            <hr />
            <label>
              Assign to inventory
              <select value={unitId} onChange={(event) => setUnitId(event.target.value)}>
                <option value="">Select a moving unit</option>
                {units.map((unit) => (
                  <option value={unit.id} key={unit.id}>
                    {unit.stock_number} — {unit.name}
                  </option>
                ))}
              </select>
            </label>
            <button className="button primary" onClick={assign} disabled={!unitId || working}>
              <CheckCircle2 size={19} /> Assign and activate
            </button>
            {unitId && (
              <a className="button secondary" href={`/api/tags/${tag.token}/label.pdf`} target="_blank">
                <Printer size={18} /> Open printable label
              </a>
            )}
            <button className="text-button" onClick={generate}>
              <RefreshCw size={17} /> Generate another unassigned tag
            </button>
          </div>
        </div>
      )}
      {message && <div className="alert info">{message}</div>}
    </div>
  );
}

